// src/modals/ResaJourneyTimeline.jsx — Lot « conversation enrichie ».
// Timeline LECTURE SEULE des envois AUTOMATIQUES (onglet Plateformes,
// message_sends) pour UNE résa — rendue dans ResaV2Modal onglet Messages.
//
// Source : endpoint admin `journey_message_projection` (requireAdmin,
// lib/messages-journal-handlers.js) — la projection RÉUTILISE les prédicats
// du moteur (via projectUpcomingRows + exports messages-engine), le client ne
// recalcule RIEN (une divergence = timeline menteuse).
//
// 3 états, liste chronologique sobre :
//   ✓ parti  (state 'sent')      — trace message_logs, date/heure réelle
//   🕐 prévu (state 'scheduled') — occurrence projetée, date + heure calculée
//   ⊘ sauté  (state 'skipped')   — conditions non remplies / fenêtre manquée
//
// v1 CONTENU (lecture seule) : chaque ligne est DÉPLIABLE → sujet + corps du
// message tel qu'il partirait / est parti pour CETTE résa. Le rendu vient du
// SERVEUR (row.content, même moteur que l'envoi — renderTemplate CJS), le
// client n'interprète RIEN :
//   - origin 'log'    : corps tracé à l'envoi (vérité de ce qui est parti) ;
//   - origin 'render' : aperçu recalculé (occurrences projetées + traces sans
//     body) — étiqueté honnêtement, doctrine JournalPreviewModal.
// Les liens générés par jeton à l'envoi apparaissent sous le placeholder
// partagé « [lien généré à l'envoi] » (#1003) — hint dédié, jamais de token.

import { useState, useEffect } from "react"
import { CheckCircle2, Clock3, MinusCircle, ChevronDown, ChevronRight, CalendarClock, Mail, Info } from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import { PREVIEW_LINK_PLACEHOLDER } from "../tabs/messages/preview-link-placeholders"

// Icône + couleur par état (source unique du langage visuel de la timeline).
const STATE_VISUALS = {
  sent: { Icon: CheckCircle2, color: () => DS.success, title: "Parti" },
  scheduled: { Icon: Clock3, color: () => DS.marine, title: "Prévu" },
  skipped: { Icon: MinusCircle, color: () => DS.textMuted, title: "Sauté" },
}

function fmtSentAt(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
}

function fmtOccurrence(occ) {
  if (!occ || !occ.date) return ""
  const d = new Date(`${occ.date}T12:00:00Z`)
  const day = Number.isNaN(d.getTime())
    ? occ.date
    : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
  const m = String(occ.time || "").match(/^(\d{1,2}):(\d{2})/)
  return m ? `${day} à ${m[1].padStart(2, "0")}h${m[2]}` : day
}

// Libellé principal d'une ligne : quoi + quand. Le `label` serveur (FR prêt)
// porte le détail (« Partira si … », « caution déjà reçue »…).
function rowWhen(row) {
  if (row.state === "sent") return fmtSentAt(row.at)
  if (row.occurrence) return fmtOccurrence(row.occurrence)
  if (row.at) return fmtSentAt(row.at)
  return ""
}

// Panneau déplié : sujet + corps du message RENDU côté serveur pour CETTE résa.
// Le client n'interprète RIEN — il affiche `row.content` tel quel (origin +
// subject + body). Provenance TOUJOURS dite (jamais implicite), style aperçu
// aligné sur JournalPreviewModal (bloc pointillé + hint de provenance).
function RowContent({ content }) {
  const stored = content.origin === "log"
  const hasLinkPlaceholder =
    content.has_link_placeholder === true ||
    (!!content.body && content.body.includes(PREVIEW_LINK_PLACEHOLDER)) ||
    (!!content.subject && content.subject.includes(PREVIEW_LINK_PLACEHOLDER))
  return (
    <div data-testid="journey-timeline-content" style={{ padding: "0 0 8px 22px" }}>
      {/* Provenance — corps tracé (vérité de l'envoi) vs aperçu recalculé */}
      <div style={{ display: "flex", gap: 6, alignItems: "flex-start", padding: "6px 9px", marginBottom: 8, background: stored ? DS.successBg : DS.cielSoft, borderRadius: 8, fontSize: 10.5, color: stored ? DS.successText : DS.marine, lineHeight: 1.45 }}>
        <Info size={12} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1.5 }} />
        <span>
          {stored
            ? "Contenu tracé à l'envoi — exactement ce qui est parti (ou aurait été simulé)."
            : "Aperçu recalculé pour cette résa avec l'état actuel — rien n'est encore parti."}
          {hasLinkPlaceholder && ` Les liens (livret, caution, formulaire) sont générés à l'envoi — ici sous « ${PREVIEW_LINK_PLACEHOLDER} ».`}
        </span>
      </div>
      {(content.subject || content.body) ? (
        <div style={{ background: "#fff", border: "1px dashed #dde3ec", borderRadius: 8, padding: 11 }}>
          {content.subject && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: DS.marineDark, marginBottom: 7, paddingBottom: 7, borderBottom: `1px solid ${DS.border}` }}>
              <Mail size={13} strokeWidth={2} /> Sujet : {content.subject}
            </div>
          )}
          <div data-testid="journey-timeline-content-body" style={{ fontSize: 12, color: "#333", lineHeight: 1.6, whiteSpace: "pre-wrap", fontFamily: DS.fontBody }}>
            {content.body || <span style={{ color: "#aaa", fontStyle: "italic" }}>(contenu vide)</span>}
          </div>
        </div>
      ) : (
        <div style={{ padding: "12px 11px", background: DS.pageBg, borderRadius: 8, fontSize: 11.5, color: DS.textSecondary, textAlign: "center" }}>
          Pas de contenu disponible pour cette ligne (message ou résa introuvable).
        </div>
      )}
    </div>
  )
}

export default function ResaJourneyTimeline({ reservationId }) {
  const [rows, setRows] = useState(null)
  const [dryRun, setDryRun] = useState(false)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(true)
  // Lignes dépliées (contenu visible). Set de row.key — plusieurs à la fois OK.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set())

  const toggleRow = (key) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setRows(null); setError(null); setExpandedKeys(new Set())
      try {
        const { data: sess } = await supabase.auth.getSession()
        const token = sess?.session?.access_token
        if (!token) throw new Error("Session admin requise")
        const res = await fetch("/api/beds24-mapping?action=journey_message_projection", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: "journey_message_projection", reservation_id: reservationId }),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`)
        if (cancelled) return
        setRows(json.rows || [])
        setDryRun(json.dry_run === true)
      } catch (e) {
        if (!cancelled) setError(e.message)
      }
    })()
    return () => { cancelled = true }
  }, [reservationId])

  return (
    <div data-testid="resa-journey-timeline" style={{ border: `1px solid ${DS.border}`, borderRadius: 8, marginBottom: 12, background: DS.card, fontFamily: DS.fontBody }}>
      <button
        type="button"
        data-testid="journey-timeline-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", textAlign: "left", background: "transparent", border: "none", padding: "9px 12px", cursor: "pointer", fontFamily: DS.fontBody }}
      >
        {open ? <ChevronDown size={14} strokeWidth={2} color={DS.textMuted} /> : <ChevronRight size={14} strokeWidth={2} color={DS.textMuted} />}
        <CalendarClock size={14} strokeWidth={2} color={DS.marine} />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: DS.marineDark }}>
          Messages automatiques
        </span>
        {Array.isArray(rows) && (
          <span style={{ fontSize: 10.5, color: DS.textMuted }}>· {rows.length}</span>
        )}
        {dryRun && (
          <span title="MESSAGES_DRY_RUN actif : les envois sont simulés (trace sans email/chat réel)." style={{ marginLeft: "auto", fontSize: 9, fontWeight: 700, letterSpacing: 0.3, color: DS.warningText, background: DS.warningBg, padding: "2px 7px", borderRadius: 5 }}>
            DRY-RUN
          </span>
        )}
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${DS.border}`, padding: "8px 12px 10px" }}>
          {error && (
            <div data-testid="journey-timeline-error" style={{ padding: "7px 10px", background: DS.dangerBg, color: DS.dangerText, borderRadius: 6, fontSize: 11.5 }}>
              {error}
            </div>
          )}
          {!error && rows === null && (
            <div style={{ padding: "6px 2px", color: DS.textMuted, fontSize: 11.5 }}>Chargement…</div>
          )}
          {!error && Array.isArray(rows) && rows.length === 0 && (
            <div data-testid="journey-timeline-empty" style={{ padding: "6px 2px", color: DS.textMuted, fontSize: 11.5 }}>
              Aucun envoi automatique pour cette réservation.
            </div>
          )}
          {!error && Array.isArray(rows) && rows.map((row) => {
            const vis = STATE_VISUALS[row.state] || STATE_VISUALS.skipped
            const Icon = vis.Icon
            const color = vis.color()
            const when = rowWhen(row)
            // Contenu servi INLINE par le serveur (row.content). Dépliable si un
            // sujet OU un corps a pu être rendu ; sinon la ligne reste statique.
            const content = row.content
            const canExpand = !!content && (!!content.subject || !!content.body)
            const isExpanded = canExpand && expandedKeys.has(row.key)
            return (
              <div
                key={row.key}
                data-testid={`journey-timeline-row-${row.state}`}
                style={{ borderBottom: `1px solid ${DS.pageBg}`, opacity: row.state === "skipped" ? 0.75 : 1 }}
              >
                <div
                  role={canExpand ? "button" : undefined}
                  tabIndex={canExpand ? 0 : undefined}
                  aria-expanded={canExpand ? isExpanded : undefined}
                  data-testid={canExpand ? "journey-timeline-row-toggle" : undefined}
                  onClick={canExpand ? () => toggleRow(row.key) : undefined}
                  onKeyDown={canExpand ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleRow(row.key) } } : undefined}
                  style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", cursor: canExpand ? "pointer" : "default" }}
                >
                  <Icon size={14} strokeWidth={2} color={color} style={{ flexShrink: 0, marginTop: 1 }} title={vis.title} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: DS.textPrimary }}>
                        {row.send?.name || row.send?.slug || "(envoi)"}
                      </span>
                      {when && <span style={{ fontSize: 10.5, color: DS.textMuted }}>{when}</span>}
                    </div>
                    <div style={{ fontSize: 10.5, color: row.state === "skipped" ? DS.textMuted : DS.textSecondary, marginTop: 1 }}>
                      {row.label}
                    </div>
                  </div>
                  {canExpand && (
                    <span style={{ flexShrink: 0, marginTop: 1, display: "inline-flex" }} title={isExpanded ? "Masquer le contenu" : "Voir le contenu"}>
                      {isExpanded
                        ? <ChevronDown size={13} strokeWidth={2} color={DS.textMuted} />
                        : <ChevronRight size={13} strokeWidth={2} color={DS.textMuted} />}
                    </span>
                  )}
                </div>
                {isExpanded && <RowContent content={content} />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
